import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { Fragment, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableRow, styled, useTheme } from '@mui/material';
import { useNavigate } from "react-router-dom";
import { BoxContainer } from '../layouts/Theme';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import { useI18N } from '../context/I18NContext';
import { useFlow } from '../context/FlowContext';
import { useSettings } from '../context/SettingsContext';
import useWidgetStore from '../store/useWidgetStore';
import useDataStore from '../store/useDataStore';
import { queryClient } from "../../../api/ApiClient";
import { isMobile } from '../../../helpers/browser';
import { moneyFormat } from '../helpers/general';
import { appointmentsInOrder, apptypeNamesFor, capacityGroupNamesFrom, capacityLabelFor, resourceNamesFor } from '../helpers/appointment';
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    textAlign: 'right',
    width: '6em',
    // A long product/apptype name in the row's other cell has nothing bounding its own width, so
    // the table's auto layout shrinks this column to make room for it - without this, that leaves
    // "€ 75,-" narrower than its own text and wraps it onto two lines instead. The name cell's
    // Typography already truncates with an ellipsis once it is the one being squeezed instead.
    whiteSpace: 'nowrap'
}));
export default function ThankYou(props) {
    const { t, ts } = useI18N();
    const navigate = useNavigate();
    const [showRestart, setShowRestart] = useState(false);
    const [dates, setDates] = useState([]);
    const [smallMode, setSmallMode] = useState(false);
    const { showSelectedResource, staticData, widgetMode, orientation } = useSettings();
    const flow = useFlow();
    const theme = useTheme();
    const { cart, booking, enabled, consumer, isGuest, setEnabled, setIsAccount, setIsGuest, setIsLoginAccount, setIsNewAccount, setSelectedApptype, setSelectedGroup, setSelectedDate, setSelectedTime, resetCart, setConsumer, resetBooking, language, setIsPaying, } = useWidgetStore();
    const { apptypes, resources, getProduct } = useDataStore();
    useEffect(() => {
        setSmallMode(orientation === "vertical" || isMobile);
        setEnabled(Object.assign(Object.assign({}, enabled), { apptype: true, resource: true }));
        setSelectedApptype();
        setSelectedGroup();
    }, []);
    useEffect(() => {
        var _a, _b, _c, _d;
        if (((_b = (_a = flow === null || flow === void 0 ? void 0 : flow.currentView) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.showRestart) !== undefined) {
            setShowRestart((_d = (_c = flow === null || flow === void 0 ? void 0 : flow.currentView) === null || _c === void 0 ? void 0 : _c.config) === null || _d === void 0 ? void 0 : _d.showRestart);
        }
    }, [flow.currentView]);
    useLayoutEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if ((_b = (_a = flow === null || flow === void 0 ? void 0 : flow.currentView) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.redirect) {
            if (widgetMode === 'standalone') {
                window.location.href = (_d = (_c = flow === null || flow === void 0 ? void 0 : flow.currentView) === null || _c === void 0 ? void 0 : _c.config) === null || _d === void 0 ? void 0 : _d.redirect;
            }
            else {
                window.parent.postMessage({
                    type: 'redirect',
                    url: (_f = (_e = flow === null || flow === void 0 ? void 0 : flow.currentView) === null || _e === void 0 ? void 0 : _e.config) === null || _f === void 0 ? void 0 : _f.redirect
                }, '*');
            }
        }
        if ((_g = flow === null || flow === void 0 ? void 0 : flow.currentView) === null || _g === void 0 ? void 0 : _g.config) {
            if ((_k = (_j = (_h = flow === null || flow === void 0 ? void 0 : flow.currentView) === null || _h === void 0 ? void 0 : _h.config) === null || _j === void 0 ? void 0 : _j.showRestart) !== null && _k !== void 0 ? _k : true) {
                // This is a useLayoutEffect, so runs after all rendering is done
                //  setShowRestart(flow?.currentView?.config?.showRestart ?? true)
            }
            else {
                setIsGuest(true);
                setIsAccount(false);
                setIsLoginAccount(false);
                setIsNewAccount(false);
            }
        }
    }, [flow.currentView]);
    useEffect(() => {
        if (isGuest && (consumer === null || consumer === void 0 ? void 0 : consumer.id)) {
            setConsumer();
        }
    }, [isGuest, consumer === null || consumer === void 0 ? void 0 : consumer.id]);
    useEffect(() => {
        var _a;
        if (((_a = booking === null || booking === void 0 ? void 0 : booking.appointments) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            let _dates = [];
            appointmentsInOrder(booking.appointments).forEach((_appointment) => {
                _dates.push(dayjs(_appointment.date).locale(language).format('dddd D MMMM HH:mm'));
            });
            setDates(_dates);
        }
    }, [booking === null || booking === void 0 ? void 0 : booking.appointments]);
    const capacityGroupNames = useMemo(() => capacityGroupNamesFrom(flow.views), [flow.views]);
    // What each appointment - not just the first - was actually booked with: its own
    // apptype/resource/capacity, read off that appointment rather than off appointment[0] plus
    // whichever apptype/capacity the pickers currently have live-selected (long since moved on to
    // whatever the consumer was doing last, which by the time this page is showing is nothing).
    const appointmentInfo = useMemo(() => appointmentsInOrder(booking === null || booking === void 0 ? void 0 : booking.appointments).map((appointment) => {
        var _a;
        return ({
            apptypeStr: apptypeNamesFor(appointment, apptypes).join(', '),
            resourceStr: resourceNamesFor(appointment, (_a = staticData === null || staticData === void 0 ? void 0 : staticData.resources) !== null && _a !== void 0 ? _a : []).join(', '),
            capacityStr: capacityLabelFor(appointment, capacityGroupNames, { t, ts }, 'summary')
        });
    }), [booking === null || booking === void 0 ? void 0 : booking.appointments, apptypes, resources, capacityGroupNames]);
    const restart = (e) => {
        let target = e.target.closest('button');
        let path = target.getAttribute('rel');
        resetBooking();
        resetCart();
        let d = new Date();
        d.setHours(0);
        d.setMinutes(0);
        setSelectedDate(d);
        setSelectedTime(d); // set the selected time explicitly to a time in the past, so the useDates and useTimes queries invalidate
        setIsPaying(false);
        queryClient.removeQueries();
        queryClient.invalidateQueries();
        setTimeout(() => {
            navigate(`/oa_widget_${path}`);
        }, 200);
    };
    const renderLine = (line, product, i) => {
        var _a;
        // `product` is only ever used below for the optional subtitle - name/quantity/price all
        // come from `line` itself. getProduct() only searches the optional-purchase catalog, so a
        // subscription/membership line's productId is never found there and product is always
        // undefined for it; bailing out on that used to skip the whole row instead of just the
        // subtitle, which is why a newly bought/mandated membership never showed up here.
        return (_jsxs(Fragment, { children: [_jsxs(TableRow, { rel: i.toString(), sx: {
                        backgroundColor: theme.palette.background.default,
                        height: '90px',
                        maxHeight: '90px'
                    }, children: [_jsxs(TableCell, { children: [_jsx(Typography, { gutterBottom: true, variant: "h5", component: "div", sx: {
                                        fontSize: '1rem',
                                        overflow: 'hidden',
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                    }, children: line.name }), (product === null || product === void 0 ? void 0 : product.subtitle) && (_jsx(Typography, { variant: "body2", sx: Object.assign({ color: 'text.secondary', overflow: 'hidden', maxWidth: '150px', textOverflow: "ellipsis", whiteSpace: "nowrap" }, (smallMode && {
                                        maxWidth: '250px',
                                    })), children: product.subtitle }))] }), _jsx(StyledTableCell, { children: line.productType === 'product' && line.origin !== "requiredPurchase" && (_jsx(Box, { children: line.quantity })) }), _jsx(StyledTableCell, { sx: { width: '6em' }, children: moneyFormat(line.quantity * line.price) })] }, line.name), (_a = cart.lines) === null || _a === void 0 ? void 0 : _a.map((requiredLine, j) => {
                    // let product = getProduct(requiredLine.productId)
                    if (requiredLine.origin !== "requiredPurchase" || requiredLine.requiredBy !== line.productId) {
                        return (_jsx(Fragment, {}, `required-${j}`));
                    }
                    return (_jsxs(TableRow, { sx: {
                            backgroundColor: theme.palette.background.default,
                            height: '32px',
                            maxHeight: '32px'
                        }, children: [_jsxs(TableCell, { sx: {
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden'
                                }, children: [" ", requiredLine.name] }), _jsx(TableCell, {}), _jsx(StyledTableCell, { children: moneyFormat(requiredLine.price) })] }, `required-${j}`));
                })] }, `line-${i}`));
    };
    const renderLines = useMemo(() => {
        var _a, _b;
        // Not `productType === 'appointment'` - that one already has its own card above ("Je
        // huidige selectie"/renderAppointments). It used to be silently excluded here too, purely
        // as a side effect of the getProduct() bail-out bug fixed in renderLine - now that a
        // subscription/membership line's row survives that fix, the appointment's has to be
        // excluded on purpose or it duplicates itself at €0,- underneath "Extra producten".
        let lines = (_b = (_a = cart.lines) === null || _a === void 0 ? void 0 : _a.filter((line) => line.origin !== "requiredPurchase" && line.productType !== 'appointment')) !== null && _b !== void 0 ? _b : [];
        if (lines.length === 0) {
            return (_jsx(_Fragment, {}));
        }
        return (_jsxs(_Fragment, { children: [orientation === "vertical" && (_jsx(Typography, { variant: "h5", sx: {
                        fontWeight: 500,
                        fontSize: '1.1rem',
                        margin: "16px 0"
                    }, children: t("Extra producten") })), _jsx(TableContainer, { id: "widget-cart-simple", sx: {
                        marginBottom: 4,
                        borderRadius: 0,
                        // overflowX: 'hidden'
                    }, component: Paper, children: _jsx(Table, { size: "small", "aria-label": t('Overzicht van je winkelwagen'), children: _jsx(TableBody, { sx: {
                                "& .MuiTableCell-body": {
                                    fontSize: '1rem'
                                }
                            }, children: lines.map((line, i) => {
                                let product = getProduct(line.productId);
                                if (line.origin === "requiredPurchase") {
                                    return (_jsx(Fragment, {}, i));
                                }
                                return (_jsx(_Fragment, { children: renderLine(line, product, i) }));
                            }) }) }) })] }));
    }, [cart.lines]);
    // The subscriptions that paid for the appointments: what a new one cost, and what each has left - the
    // one the consumer already had, and the one bought with this order. The order's own lines do not
    // carry that purchase when the consumer topped up an existing card, so it is listed here; when they
    // do (a subscription bought outright is a line like any other) it is left out so it shows once.
    const renderSubscriptions = useMemo(() => {
        var _a, _b;
        let subscriptions = (_a = cart.subscriptions) !== null && _a !== void 0 ? _a : [];
        if (subscriptions.length === 0) {
            return (_jsx(_Fragment, {}));
        }
        let purchaseInLines = ((_b = cart.lines) !== null && _b !== void 0 ? _b : []).some((line) => ['subscription', 'membership'].includes(line.productType));
        let purchases = purchaseInLines ? [] : subscriptions.filter((subscription) => { var _a; return subscription.bought && ((_a = subscription.price) !== null && _a !== void 0 ? _a : 0) > 0; });
        return (_jsx(TableContainer, { id: "widget-cart-subscriptions", sx: {
                marginBottom: 4,
                borderRadius: 0,
            }, component: Paper, children: _jsx(Table, { size: "small", "aria-label": t('Overzicht van je winkelwagen'), children: _jsxs(TableBody, { sx: {
                        "& .MuiTableCell-body": {
                            fontSize: '1rem'
                        }
                    }, children: [purchases.map((subscription) => {
                            var _a;
                            return (_jsxs(TableRow, { sx: {
                                    backgroundColor: theme.palette.background.default
                                }, children: [_jsx(TableCell, { children: _jsx(Typography, { variant: "h5", component: "div", sx: {
                                                fontSize: '1rem',
                                                overflow: 'hidden',
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap"
                                            }, children: subscription.name }) }), _jsx(StyledTableCell, {}), _jsx(StyledTableCell, { children: moneyFormat((_a = subscription.price) !== null && _a !== void 0 ? _a : 0) })] }, `purchase-${subscription.id}`));
                        }), subscriptions.map((subscription) => {
                            var _a;
                            return (_jsx(TableRow, { sx: {
                                    backgroundColor: theme.palette.background.default
                                }, children: _jsxs(TableCell, { colSpan: 3, children: [_jsx(Typography, { variant: "h5", component: "div", sx: {
                                                fontSize: '1rem',
                                                overflow: 'hidden',
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap"
                                            }, children: subscription.name }), _jsx(Typography, { variant: "body2", sx: { color: 'text.secondary' }, children: subscription.type === 'membership'
                                                // no credits to count: what it did was pay for appointments
                                                ? ts('%amount% %amount=1?afspraak:afspraken% betaald met je abonnement', { amount: (_a = subscription.appointments) !== null && _a !== void 0 ? _a : 0 })
                                                : subscription.balance < 0
                                                    ? t('Onbeperkt')
                                                    : `${subscription.balance} ${t('strippen beschikbaar')}` })] }) }, `balance-${subscription.id}`));
                        })] }) }) }));
    }, [cart.subscriptions, cart.lines]);
    const renderAppointments = useMemo(() => {
        return (_jsx(_Fragment, { children: dates.map((date, i) => {
                var _a, _b, _c, _d, _e, _f;
                return (_jsxs(Fragment, { children: [_jsx(Typography, { variant: "subtitle2", sx: {
                                fontWeight: 700,
                                fontSize: '1.1rem',
                            }, children: date }), ((_a = appointmentInfo[i]) === null || _a === void 0 ? void 0 : _a.apptypeStr) !== '' && (_jsx(Typography, { variant: "subtitle2", sx: {
                                fontWeight: 500,
                                fontSize: '1.1rem',
                            }, children: (_b = appointmentInfo[i]) === null || _b === void 0 ? void 0 : _b.apptypeStr })), showSelectedResource && ((_c = appointmentInfo[i]) === null || _c === void 0 ? void 0 : _c.resourceStr) !== '' && (_jsx(Typography, { variant: "subtitle2", sx: {
                                fontWeight: 500,
                                fontSize: '1.1rem',
                            }, children: (_d = appointmentInfo[i]) === null || _d === void 0 ? void 0 : _d.resourceStr })), ((_e = appointmentInfo[i]) === null || _e === void 0 ? void 0 : _e.capacityStr) !== '' && (_jsx(Typography, { variant: "subtitle2", sx: {
                                fontWeight: 500,
                                fontSize: '1.1rem',
                            }, children: (_f = appointmentInfo[i]) === null || _f === void 0 ? void 0 : _f.capacityStr }))] }, `appointment-${i}`));
            }) }));
    }, [dates, appointmentInfo, showSelectedResource]);
    const renderRequiredAppProducts = useMemo(() => {
        var _a, _b;
        let lines = (_b = (_a = cart.lines) === null || _a === void 0 ? void 0 : _a.filter((line) => line.origin === "requiredPurchase" && line.requiredBy === "appointment")) !== null && _b !== void 0 ? _b : [];
        if (lines.length === 0) {
            return (_jsx(_Fragment, {}));
        }
        return (_jsx(_Fragment, { children: lines.map((line, i) => (_jsx(Typography, { variant: "subtitle2", sx: {
                    fontWeight: 500,
                    fontSize: '1.1rem',
                }, children: line.name }, i))) }));
    }, [cart.lines]);
    return (_jsxs(BoxContainer, { children: [_jsx(Typography, { variant: "h6", sx: { color: theme.palette.text.primary }, children: t(orientation === "vertical" ? 'Bedankt voor de reservering' : 'Bedankt voor je aankoop') }), _jsx(Typography, { variant: "subtitle2", sx: { color: theme.palette.text.primary }, children: t('') }), _jsxs(Alert, { sx: {
                    my: 3,
                    color: theme.palette.text.primary
                }, severity: "success", variant: "outlined", children: [orientation === "vertical" && (_jsxs(_Fragment, { children: [_jsxs(Typography, { variant: "caption", sx: {
                                    fontWeight: 500,
                                    fontSize: '0.9rem'
                                }, children: [t('Je huidige selectie', 'nav'), ":"] }), renderAppointments, renderRequiredAppProducts] })), orientation === "horizontal" && (_jsx(_Fragment, { children: _jsx(Typography, { variant: "caption", sx: {
                                fontWeight: 500,
                                fontSize: '0.9rem'
                            }, children: t('Je aankoop is voltooid', 'nav') }) }))] }), renderLines, renderSubscriptions, showRestart && (_jsx(Button, { onClick: restart, rel: 'index', fullWidth: true, variant: "contained", children: t(orientation === "vertical" ? 'Nog een afspraak maken' : "Verder winkelen") }))] }));
}
